/**
 * TakeoffPinLayer - Pins linking structure take-offs to PDF locations
 */

import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Package, X } from 'lucide-react';
import type { PdfTakeoffLink, PdfTakeoffPin } from '../../types';
import { formatCrossSection, formatTimberOrderLength } from '../../utils/timberTakeoffHelpers';
import './TakeoffPinLayer.css';

export interface StructureTimberHoverItem {
  id: string;
  name: string;
  crossSection: string;
  length: string;
}

function formatTimberLength(link: PdfTakeoffLink): string {
  const orderLength = formatTimberOrderLength(link.dimensions);
  if (orderLength) return orderLength;
  if (link.measuredLength != null && !Number.isNaN(link.measuredLength)) {
    if (link.measuredLengthUnit === 'in') return `${link.measuredLength}"`;
    return `${link.measuredLength}'`;
  }
  return '—';
}

interface TakeoffPinLayerProps {
  pageNumber: number;
  pins: PdfTakeoffPin[];
  takeoffLinks?: PdfTakeoffLink[];
  pinMode: { structureId: string; structureName: string } | null;
  onPlacePin: (pageNumber: number, x: number, y: number) => void;
  onPinClick: (pin: PdfTakeoffPin) => void;
  onMovePin?: (pin: PdfTakeoffPin, x: number, y: number) => void;
  onCancelPinMode?: () => void;
  onDeletePin?: (pin: PdfTakeoffPin) => void;
  /** When false, saved pins are display-only (still clickable). */
  allowPinDrag?: boolean;
  allowPinDelete?: boolean;
}

const DRAG_THRESHOLD_PX = 4;
const TIMBER_HOVER_DELAY_MS = 1500;

function clampPercent(n: number): number {
  return Math.max(0, Math.min(100, n));
}

interface PinMarkerProps {
  pin: PdfTakeoffPin;
  timberItems: StructureTimberHoverItem[];
  draggable: boolean;
  layerRef: React.RefObject<HTMLDivElement | null>;
  onPinClick: (pin: PdfTakeoffPin) => void;
  onMovePin?: (pin: PdfTakeoffPin, x: number, y: number) => void;
  onDeletePin?: (pin: PdfTakeoffPin) => void;
  deletable: boolean;
}

function PinMarker({
  pin,
  timberItems,
  draggable,
  deletable,
  layerRef,
  onPinClick,
  onMovePin,
  onDeletePin,
}: PinMarkerProps) {
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  const clientToPercent = useCallback(
    (clientX: number, clientY: number) => {
      const rect = layerRef.current?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return null;
      return {
        x: clampPercent(((clientX - rect.left) / rect.width) * 100),
        y: clampPercent(((clientY - rect.top) / rect.height) * 100),
      };
    },
    [layerRef],
  );

  const handlePointerDown = (e: React.PointerEvent) => {
    if (!draggable || !pin.id) return;
    e.stopPropagation();
    e.preventDefault();
    clearHoverTimer();
    setExpanded(false);
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, moved: false };
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current || !draggable) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      dragRef.current.moved = true;
    }
    const pos = clientToPercent(e.clientX, e.clientY);
    if (pos) setDragPos(pos);
  };

  const finishDrag = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const { moved } = dragRef.current;
    dragRef.current = null;
    setIsDragging(false);

    const pos = dragPos ?? clientToPercent(e.clientX, e.clientY);
    setDragPos(null);

    if (moved && pos && onMovePin) {
      onMovePin(pin, pos.x, pos.y);
    } else if (!moved) {
      onPinClick(pin);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    e.stopPropagation();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    finishDrag(e);
  };

  const handlePointerCancel = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    setIsDragging(false);
    setDragPos(null);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const x = dragPos?.x ?? pin.x;
  const y = dragPos?.y ?? pin.y;
  const showTimberList = expanded && !isDragging && timberItems.length > 0;

  const handlePointerEnter = () => {
    if (isDragging || timberItems.length === 0) return;
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => setExpanded(true), TIMBER_HOVER_DELAY_MS);
  };

  const handlePointerLeave = () => {
    clearHoverTimer();
    setExpanded(false);
  };

  return (
    <div
      className={`takeoff-pin-wrapper ${showTimberList ? 'takeoff-pin-wrapper--expanded' : ''} ${isDragging ? 'takeoff-pin-wrapper--dragging' : ''}`}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)',
      }}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
    >
      <div
        className={`takeoff-pin ${showTimberList ? 'takeoff-pin--expanded' : ''} ${draggable ? 'takeoff-pin-draggable' : ''} ${isDragging ? 'takeoff-pin-dragging' : ''}`}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={e => e.stopPropagation()}
      >
        <div className="takeoff-pin-header">
          <Package size={16} />
          <span className="takeoff-pin-label">{pin.structureName}</span>
          {deletable && pin.id && !pin.id.startsWith('virtual-') && onDeletePin && (
            <button
              type="button"
              className="takeoff-pin-delete"
              title={`Remove ${pin.structureName} label`}
              onPointerDown={e => e.stopPropagation()}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                onDeletePin(pin);
              }}
            >
              <X size={12} />
            </button>
          )}
        </div>
        {showTimberList && (
          <ul className="takeoff-pin-timber-list">
            {timberItems.map(item => (
              <li key={item.id} className="takeoff-pin-timber-row">
                <span className="takeoff-pin-timber-name">{item.name}</span>
                <span className="takeoff-pin-timber-section">{item.crossSection}</span>
                <span className="takeoff-pin-timber-length">{item.length}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function TakeoffPinLayer({
  pageNumber,
  pins,
  takeoffLinks = [],
  pinMode,
  onPlacePin,
  onPinClick,
  onMovePin,
  onCancelPinMode,
  onDeletePin,
  allowPinDrag = true,
  allowPinDelete = false,
}: TakeoffPinLayerProps) {
  const layerRef = useRef<HTMLDivElement>(null);

  const timberItemsByStructureId = useMemo(() => {
    const map = new Map<string, StructureTimberHoverItem[]>();
    for (const link of takeoffLinks) {
      const crossSection = formatCrossSection(link.dimensions);
      const name =
        link.quantity > 1
          ? `${link.name || '—'} ×${link.quantity}`
          : link.name || '—';
      const list = map.get(link.structureId) ?? [];
      list.push({
        id: link.id || `${link.measurementId}-${list.length}`,
        name,
        crossSection: crossSection || '—',
        length: formatTimberLength(link),
      });
      map.set(link.structureId, list);
    }
    Array.from(map.values()).forEach(list => {
      list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    });
    return map;
  }, [takeoffLinks]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    if (pinMode) {
      onPlacePin(pageNumber, x, y);
    }
  };

  const pagePins = pins.filter(p => p.pageNumber === pageNumber);
  const draggable = allowPinDrag && !pinMode && !!onMovePin;
  const deletable = allowPinDelete && !pinMode && !!onDeletePin;

  return (
    <div
      ref={layerRef}
      className={`takeoff-pin-layer ${pinMode ? 'takeoff-pin-mode' : ''}`}
      style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={pinMode ? handleClick : undefined}
    >
      {pinMode && (
        <div className="takeoff-pin-mode-hint">
          <span>Click the PDF to add &quot;{pinMode.structureName}&quot;</span>
          {onCancelPinMode && (
            <button type="button" className="takeoff-pin-mode-cancel" onClick={onCancelPinMode}>
              Cancel
            </button>
          )}
        </div>
      )}
      {pagePins.map((pin, index) => (
        <PinMarker
          key={pin.id || `${pin.structureId}-${pin.pageNumber}-${index}-${pin.x}-${pin.y}`}
          pin={pin}
          timberItems={timberItemsByStructureId.get(pin.structureId) ?? []}
          draggable={draggable && !!pin.id}
          deletable={deletable && !!pin.id}
          layerRef={layerRef}
          onPinClick={onPinClick}
          onMovePin={onMovePin}
          onDeletePin={onDeletePin}
        />
      ))}
    </div>
  );
}
